import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '../../../components/ui/Button'
import { useAuthStore } from '../../../stores/authStore'
import api from '../../../lib/api'

export default function OTPVerifyPage() {
  const navigate = useNavigate()
  const { pendingIdentifier, pendingMethod, setAuth, clearPending } = useAuthStore()
  const [otp, setOtp] = useState(['', '', '', ''])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resendTimer, setResendTimer] = useState(30)
  const [canResend, setCanResend] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Redirect if no pending auth
  useEffect(() => {
    if (!pendingIdentifier) {
      navigate('/auth/login')
    }
  }, [pendingIdentifier, navigate])

  // Countdown timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000)
      return () => clearTimeout(timer)
    } else {
      setCanResend(true)
    }
  }, [resendTimer])

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value.slice(-1)
    setOtp(newOtp)
    setError('')

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when complete (4 digits)
    if (newOtp.every(d => d) && newOtp.join('').length === 4) {
      handleVerify(newOtp.join(''))
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4)
    if (pasted.length === 4) {
      setOtp(pasted.split(''))
      handleVerify(pasted)
    }
  }

  const handleVerify = async (code: string) => {
    setLoading(true)
    setError('')

    try {
      const { data } = await api.post('/auth/verify-otp', {
        identifier: pendingIdentifier,
        otp: code,
        type: pendingMethod,
      })

      setAuth(data.user, data.access_token, data.refresh_token)
      clearPending()
      navigate('/')
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      setError(error.response?.data?.detail || 'Invalid OTP. Please try again.')
      setOtp(['', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (!canResend) return

    setCanResend(false)
    setResendTimer(30)
    setError('')

    try {
      await api.post('/auth/request-otp', {
        identifier: pendingIdentifier,
        type: pendingMethod,
      })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      setError(error.response?.data?.detail || 'Failed to resend OTP')
    }
  }

  const maskedIdentifier = pendingMethod === 'phone'
    ? `+91 ****${pendingIdentifier?.slice(-4)}`
    : pendingIdentifier?.replace(/(.{2}).*(@.*)/, '$1****$2')

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <button
          onClick={() => { clearPending(); navigate('/auth/login') }}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
        >
          <i className="fas fa-arrow-left"></i>
          <span>Back to Login</span>
        </button>

        {/* Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 text-primary-600 rounded-full mb-4">
              <i className="fas fa-shield-alt text-2xl"></i>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Verify OTP</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Enter the 4-digit code sent to<br />
              <span className="font-medium text-gray-900 dark:text-white">{maskedIdentifier}</span>
            </p>
          </div>

          {/* OTP Input */}
          <div className="flex justify-center gap-3 sm:gap-4 mb-6" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-14 h-16 sm:w-16 sm:h-18 text-center text-2xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                autoFocus={index === 0}
                disabled={loading}
              />
            ))}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400 text-center">
                <i className="fas fa-exclamation-circle mr-2"></i>
                {error}
              </p>
            </div>
          )}

          {/* Verify Button */}
          <Button
            onClick={() => handleVerify(otp.join(''))}
            fullWidth
            loading={loading}
            disabled={otp.some(d => !d)}
          >
            <i className="fas fa-check mr-2"></i>
            Verify & Sign In
          </Button>

          {/* Resend */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Didn't receive the code?{' '}
              {canResend ? (
                <button
                  onClick={handleResend}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  Resend OTP
                </button>
              ) : (
                <span className="text-gray-500">
                  Resend in {resendTimer}s
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Security Note */}
        <div className="mt-6 flex items-start gap-3 p-4 bg-white/50 dark:bg-gray-800/50 rounded-xl">
          <i className="fas fa-lock text-primary-600 mt-0.5"></i>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            For your security, the OTP expires in 5 minutes. Never share your OTP with anyone.
          </p>
        </div>
      </div>
    </div>
  )
}
